import React, { useState } from "react";
import { Bell, Clock, CalendarDays, Repeat, ListMusic, Smile } from "lucide-react";
import locales from "../utils/locales";

function t(key, lang, fallback) {
  return (locales?.[lang]?.[key]) || fallback;
}

export default function CustomReminderForm({ onSave, onCancel, lang = "en" }) {
  const L = locales[lang] || locales.en;

  // Day mapping: JS Date.getDay() => [0=Sun,...,6=Sat]
  const WEEKDAYS = [
    { id: 0, label: L.sun || "Sun" },
    { id: 1, label: L.mon || "Mon" },
    { id: 2, label: L.tue || "Tue" },
    { id: 3, label: L.wed || "Wed" },
    { id: 4, label: L.thu || "Thu" },
    { id: 5, label: L.fri || "Fri" },
    { id: 6, label: L.sat || "Sat" },
  ];

  const CATEGORIES = lang === "th"
    ? ["กิจกรรม", "นิสัย", "แรงบันดาลใจ", "อื่น ๆ"]
    : ["Activity", "Habit", "Motivation", "Other"];
  const SOUNDS = ["Default", "Chime", "Bell", "Silent"];

  const [message, setMessage] = useState("");
  const [time, setTime] = useState("08:00");
  // ตั้งค่า default เป็น specificDays
  const [repeatType, setRepeatType] = useState("specificDays");
  const [selectedDays, setSelectedDays] = useState([]); // สำหรับ specificDays
  const [specificDate, setSpecificDate] = useState("");   // สำหรับ specificDate
  const [dateRange, setDateRange] = useState({ from: "", to: "" }); // สำหรับ dateRange
  const [category, setCategory] = useState(CATEGORIES[0]);
  const [sound, setSound] = useState(SOUNDS[0]);

  // Weekday select
  const toggleDay = (id) => {
    setSelectedDays((prev) =>
      prev.includes(id) ? prev.filter((d) => d !== id) : [...prev, id]
    );
  };

  // Validate
  const isValid =
    message.trim() &&
    time &&
    (
      (repeatType === "specificDays" && selectedDays.length > 0) ||
      (repeatType === "specificDate" && specificDate) ||
      (repeatType === "dateRange" && dateRange.from && dateRange.to)
    );

  // Submit handler
  const handleSubmit = (e) => {
    e.preventDefault();
    if (!isValid) return;

    let reminderObj = {
      message,
      category,
      sound,
      notified: false,
      repeatType,
      time: "",
      repeatConfig: {},
    };

    if (repeatType === "specificDays") {
      reminderObj.time = time; // "08:00"
      reminderObj.repeatConfig = { days: selectedDays.sort() };
    } else if (repeatType === "specificDate") {
      reminderObj.time = `${specificDate}T${time}`; // e.g. "2024-07-25T09:30"
    } else if (repeatType === "dateRange") {
      reminderObj.time = `${dateRange.from}T${time}`; // first date
      reminderObj.repeatConfig = { end: dateRange.to };
    }

    onSave(reminderObj);
  };

  return (
    <form className="p-4 rounded-2xl shadow bg-white max-w-md mx-auto" onSubmit={handleSubmit}>

      {/* Message */}
      <label className="block mb-2 font-bold flex gap-2 items-center">
        <Bell size={18} className="text-yellow-500" />
        {t("notification_message", lang, lang === "th" ? "ข้อความแจ้งเตือน" : "Notification Message")}
      </label>
      <input
        type="text"
        className="w-full p-2 border rounded mb-3"
        value={message}
        onChange={e => setMessage(e.target.value)}
        required
      />

      {/* Time */}
      <label className="block mb-2 font-bold flex gap-2 items-center">
        <Clock size={18} className="text-blue-400" />
        {t("time", lang, lang === "th" ? "เวลา" : "Time")}
      </label>
      <input
        type="time"
        className="w-40 p-2 border rounded mb-3"
        value={time}
        onChange={e => setTime(e.target.value)}
        required
      />

      {/* Repeat */}
      <label className="block mb-2 font-bold flex gap-2 items-center">
        <Repeat size={18} className="text-green-500" />
        {t("repeat", lang, lang === "th" ? "ความถี่" : "Repeat")}
      </label>
      <div className="flex flex-col gap-1 mb-2">
        {/* Specific days */}
        <label className="flex gap-2 items-center">
          <input
            type="radio"
            name="repeatType"
            value="specificDays"
            checked={repeatType === "specificDays"}
            onChange={() => setRepeatType("specificDays")}
          />
          {lang === "th" ? "เลือกวัน" : "Specific days"}
          {repeatType === "specificDays" && (
            <span className="ml-2 flex gap-1 flex-wrap">
              {WEEKDAYS.map((d) => (
                <button
                  type="button"
                  key={d.id}
                  className={`px-2 py-1 rounded border mx-1 transition ${
                    selectedDays.includes(d.id)
                      ? "bg-blue-200 border-blue-400 font-bold"
                      : "bg-gray-100 border-gray-200"
                  }`}
                  onClick={() => toggleDay(d.id)}
                  aria-pressed={selectedDays.includes(d.id)}
                >
                  {d.label}
                </button>
              ))}
            </span>
          )}
        </label>
        {/* Specific date */}
        <label className="flex gap-2 items-center">
          <input
            type="radio"
            name="repeatType"
            value="specificDate"
            checked={repeatType === "specificDate"}
            onChange={() => setRepeatType("specificDate")}
          />
          {lang === "th" ? "เลือกวันเดียว" : "Specific date"}
          {repeatType === "specificDate" && (
            <span className="ml-2 flex items-center gap-2">
              <CalendarDays size={16} className="text-purple-400" />
              <input
                type="date"
                className="border rounded p-1"
                value={specificDate}
                onChange={e => setSpecificDate(e.target.value)}
                min={new Date().toISOString().split("T")[0]}
                required
              />
            </span>
          )}
        </label>
        {/* Date range */}
        <label className="flex gap-2 items-center">
          <input
            type="radio"
            name="repeatType"
            value="dateRange"
            checked={repeatType === "dateRange"}
            onChange={() => setRepeatType("dateRange")}
          />
          {lang === "th" ? "เลือกช่วงวัน" : "Date range"}
          {repeatType === "dateRange" && (
            <span className="ml-2 flex gap-2">
              <CalendarDays size={16} className="text-purple-400" />
              <input
                type="date"
                className="border rounded p-1"
                value={dateRange.from}
                onChange={e => setDateRange({ ...dateRange, from: e.target.value })}
                required
              />
              <span>{lang === "th" ? "ถึง" : "to"}</span>
              <input
                type="date"
                className="border rounded p-1"
                value={dateRange.to}
                onChange={e => setDateRange({ ...dateRange, to: e.target.value })}
                required
                min={dateRange.from}
              />
            </span>
          )}
        </label>
      </div>

      {/* Category */}
      <label className="block mb-2 font-bold flex gap-2 items-center">
        <ListMusic size={18} className="text-pink-500" />
        {lang === "th" ? "หมวด" : "Category"}
      </label>
      <select
        className="w-full p-2 border rounded mb-3"
        value={category}
        onChange={e => setCategory(e.target.value)}
      >
        {CATEGORIES.map((c) => (
          <option key={c} value={c}>{c}</option>
        ))}
      </select>

      {/* Sound */}
      <label className="block mb-2 font-bold flex gap-2 items-center">
        <Smile size={18} className="text-orange-400" />
        {lang === "th" ? "เสียงแจ้งเตือน" : "Notification Sound"}
      </label>
      <select
        className="w-full p-2 border rounded mb-3"
        value={sound}
        onChange={e => setSound(e.target.value)}
      >
        {SOUNDS.map((s) => (
          <option key={s} value={s}>{s}</option>
        ))}
      </select>

      {/* Preview */}
      <div className="my-3 p-3 border-l-4 border-blue-400 bg-blue-50 rounded">
        <strong>
          {lang === "th" ? "ตัวอย่างแจ้งเตือน" : "Preview Notification"}:
        </strong>
        <div className="mt-1">
          <span role="img" aria-label="bell">🔔</span> {message} <br />
          <small>
            {(() => {
              if (repeatType === "specificDays") {
                const labels = selectedDays.map(i => WEEKDAYS.find(d => d.id === i)?.label).join(", ");
                return (lang === "th"
                  ? "วัน: " + labels
                  : "On: " + labels) + ` | ${time}`;
              }
              if (repeatType === "specificDate") {
                return (lang === "th" ? `วันที่: ` : "On: ") + specificDate + ` | ${time}`;
              }
              if (repeatType === "dateRange") {
                return (lang === "th"
                  ? `ช่วงวันที่: ${dateRange.from} ถึง ${dateRange.to}`
                  : `From ${dateRange.from} to ${dateRange.to}`) + ` | ${time}`;
              }
              return "";
            })()}
          </small>
        </div>
      </div>

      <div className="flex gap-4 justify-end">
        <button
          type="button"
          onClick={onCancel}
          className="px-4 py-2 rounded bg-gray-200 hover:bg-gray-300"
        >
          {t("cancel", lang, lang === "th" ? "ยกเลิก" : "Cancel")}
        </button>
        <button
          type="submit"
          disabled={!isValid}
          className="px-4 py-2 rounded bg-blue-500 text-white hover:bg-blue-600 disabled:opacity-50"
        >
          {t("save", lang, lang === "th" ? "บันทึก" : "Save")}
        </button>
      </div>
    </form>
  );
}